import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  registerUser,
  loginUser,
  fetchCartItems,
} from "../services/api";
import { AuthUser } from "../utiles/types";

const Authentication: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLogin = location.pathname === "/auth/login";

  // State declarations remain the same
  const [forgotPassword, setForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    password2: "",
    terms_accepted: false,
    referral_code: "",
    gender: "",
  });
  const [errors, setErrors] = useState({} as Record<string, string>);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");



  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    const newErrors = {
      firstName: formData.firstName ? "" : "First Name is required",
      lastName: formData.lastName ? "" : "Last Name is required",
      email: formData.email ? "" : "Email is required",
      phoneNumber: formData.phoneNumber ? "" : "PhoneNumber is required",
      password: formData.password ? "" : "Password is required",
      password2:
        formData.password2 === formData.password ? "" : "Passwords must match",
    };

    setErrors(newErrors);
    if (!Object.values(newErrors).some((error) => error)) {
      setLoading(true);
      try {
        const userData: AuthUser = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.phoneNumber,
          email: formData.email,
          password: formData.password,
          password2: formData.password2,
          isAdmin: 0,
          phone_verified: true, // Always set to true since OTP is removed
          terms_accepted: true,
          referral_code: formData.referral_code,
          gender: formData.gender,
        };
        console.log(userData)
        const response = await registerUser(userData);
        localStorage.setItem("user", JSON.stringify(response));
        setSuccessMessage("Account created successfully! Please log in.");
        navigate("/auth/login");
      } catch (error) {
        setApiError(
          error instanceof Error ? error.message : "An unknown error occurred."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError("");

    try {
      // Login user by sending email and password
      const response = await loginUser({
        email: formData.email,
        password: formData.password,
        isAdmin: 0,
      });

      localStorage.setItem("user", JSON.stringify(response));
      // Fetch and store cart items for the regular user
      const cartItems = fetchCartItems();
      localStorage.setItem("cart", JSON.stringify(cartItems));

      // Navigate to the previous state or user profile
      const previousState = location.state?.from || "/user-profile";
      navigate(previousState, {
        state: { products: location.state?.products || [] },
      });
    } catch (error) {
      setApiError("Invalid credentials. Please check your email and password.");
    }
  };

  const inputClasses =
    "w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-black focus:ring-1 focus:ring-black transition duration-200 text-gray-900 text-base";
  const buttonClasses =
    "w-full py-3 px-4 rounded-lg bg-black text-white hover:bg-gray-800 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed";
  const linkClasses =
    "text-sm text-gray-600 hover:text-black transition duration-200";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-gray-600">
            {isLogin
              ? "Please sign in to continue"
              : "Fill in your details to get started"}
          </p>
        </div>

        {/* Status Messages */}
        {(apiError || successMessage) && (
          <div
            className={`p-4 rounded-lg ${
              apiError ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
            }`}
          >
            {apiError || successMessage}
          </div>
        )}

        {/* Forms */}
        {isLogin ? (
          forgotPassword ? (
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              <div>
                <input
                  type="email"
                  id="email"
                  className={inputClasses}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <button type="submit" className={buttonClasses}>
                Reset Password
              </button>
              <button
                type="button"
                onClick={() => setForgotPassword(false)}
                className={`${linkClasses} block mx-auto`}
              >
                Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleLoginSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  id="email"
                  className={inputClasses}
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <input
                  type="password"
                  id="password"
                  className={inputClasses}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
              </div>
              <button
                type="submit"
                className={buttonClasses}
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setForgotPassword(true)}
                  className={linkClasses}
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/auth/signup")}
                  className={linkClasses}
                >
                  Create account
                </button>
              </div>
            </form>
          )
        ) : (
          <form onSubmit={handleSignupSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  type="text"
                  id="firstName"
                  name="first_name"
                  className={inputClasses}
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  id="lastName"
                  name="last_name"
                  className={inputClasses}
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>

            <div>
              <input
                type="email"
                id="email"
                name="email"
                className={inputClasses}
                placeholder="Email address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <input
                type="tel"
                id="phoneNumber"
                className={inputClasses}
                placeholder="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
            </div>

            <div>
              <select
                id="gender"
                name="gender"
                className={inputClasses}
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="" disabled>
                  Select Gender
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
              )}
            </div>

            <div>
              <input
                type="text"
                id="referralCode"
                name="referral_code"
                className={inputClasses}
                placeholder="Referral Code (optional)"
                value={formData.referral_code}
                onChange={handleInputChange}
              />
              {errors.referral_code && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.referral_code}
                </p>
              )}
            </div>
            <div>
              <input
                type="password"
                id="password"
                name="password"
                className={inputClasses}
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <input
                type="password"
                id="password2"
                name="password2"
                className={inputClasses}
                placeholder="Confirm Password"
                value={formData.password2}
                onChange={handleInputChange}
              />
              {errors.password2 && (
                <p className="mt-1 text-sm text-red-600">{errors.password2}</p>
              )}
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="termsAccepted"
                name="terms_accepted"
                className="mr-2"
                checked={formData.terms_accepted}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    terms_accepted: e.target.checked,
                  }))
                }
              />
              <label htmlFor="termsAccepted" className="text-sm">
                I accept the terms and conditions
              </label>
              {errors.terms_accepted && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.terms_accepted}
                </p>
              )}
            </div>

            <button type="submit" className={buttonClasses} disabled={loading}>
              {loading ? "Creating account..." : "Create Account"}
            </button>

            <p className="text-center text-sm text-gray-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/auth/login")}
                className="text-black hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Authentication;
